import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import { useReducedMotion } from "@/hooks/useReducedMotion";

interface AnimatedNumberProps {
  /** The target numeric value to display. */
  value: number;
  /** Formats the (interpolated) numeric value into a display string. */
  format: (n: number) => string;
  /** Animation duration in ms (default 600). */
  duration?: number;
  /**
   * When true, briefly flashes green on an increase and red on a decrease.
   * Defaults to true.
   */
  flash?: boolean;
  className?: string;
}

type FlashDir = "up" | "down" | null;

/**
 * A number that smoothly counts from its previous value to a new one, with an
 * optional color flash indicating the direction of change. Respects
 * `prefers-reduced-motion` by snapping instantly with no flash.
 */
export function AnimatedNumber({
  value,
  format,
  duration = 600,
  flash = true,
  className,
}: AnimatedNumberProps) {
  const reduced = useReducedMotion();
  const [display, setDisplay] = useState(value);
  const [dir, setDir] = useState<FlashDir>(null);

  const fromRef = useRef(value);
  const rafRef = useRef<number | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const from = fromRef.current;
    const to = value;

    if (from === to) return;

    // Direction flash.
    if (flash) {
      setDir(to > from ? "up" : "down");
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setDir(null), 700);
    }

    if (reduced || duration <= 0) {
      setDisplay(to);
      fromRef.current = to;
      return;
    }

    const start = performance.now();

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      const current = from + (to - from) * eased;
      setDisplay(current);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setDisplay(to);
        fromRef.current = to;
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      fromRef.current = to;
    };
  }, [value, duration, flash, reduced]);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, []);

  return (
    <span
      className={cn(
        "tabular-nums transition-colors duration-300",
        dir === "up" && "text-primary",
        dir === "down" && "text-destructive",
        className,
      )}
    >
      {format(display)}
    </span>
  );
}
